import { Box, Grid, TextField, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { AyceVendorCampaign, CampaignCategory, VendorCampaignStatus } from 'apis/ayceCampaign';
import { useCopier } from 'components/hooks/useCopier';
import { getStorefrontUrl } from 'helpers/urlHelper';
import { Vendor } from 'models/vendor';
import * as React from 'react';
import { useMemo } from 'react';

type VendorAyceCampaignUrlsProps = {
    vendor: Vendor;
    vendorCampaign?: AyceVendorCampaign;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            marginTop: theme.spacing(6),
            marginBottom: theme.spacing(4),
        },
        title: {
            fontSize: theme.typography.pxToRem(18),
            color: theme.palette.primary.main,
            fontWeight: 'bold',
        },
    }),
);

export default function VendorAyceCampaignUrls({
    vendor,
    vendorCampaign,
}: VendorAyceCampaignUrlsProps): React.ReactElement {
    const classes = useStyles();

    const [copiedText, onCopy] = useCopier();

    const { isVisible, merchandising, crm } = useMemo(() => {
        if (!vendorCampaign || vendorCampaign.status !== VendorCampaignStatus.PUBLISHED)
            return {
                isVisible: false,
            };

        const merchandising = vendorCampaign.attributions.find(a => a.category === CampaignCategory.MERCHANDISING);
        const crm = vendorCampaign.attributions.find(a => a.category === CampaignCategory.CRM);

        const getLink = (utm: string) =>
            getStorefrontUrl(vendor.name, `lp/?ayceCampaign=${vendorCampaign.campaignId}&utm_campaign=${utm}`);

        return {
            isVisible: true,
            merchandising: merchandising && getLink(merchandising.id),
            crm: crm && getLink(crm.id),
        };
    }, [vendor, vendorCampaign]);

    return (
        <Box className={classes.container} data-cy="Ayce-CampaignUrls">
            <Box component="h2" className={classes.title}>
                Campaign URLs
            </Box>
            {isVisible && (
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        {merchandising && (
                            <TextField
                                name="merchandising"
                                label="Attribution URL - Merchandising"
                                fullWidth
                                value={merchandising}
                                inputProps={{ readOnly: true }}
                                InputLabelProps={{ shrink: true }}
                                onClick={() => onCopy(merchandising)}
                                data-cy="Ayce-CampaignUrls-Merchandising"
                                helperText={copiedText === merchandising && 'Copied to clipboard'}
                            />
                        )}
                    </Grid>
                    <Grid item xs={10}>
                        {crm && (
                            <TextField
                                name="crm"
                                label="Attribution URL - CRM"
                                fullWidth
                                value={crm}
                                inputProps={{ readOnly: true }}
                                InputLabelProps={{ shrink: true }}
                                onClick={() => onCopy(crm)}
                                data-cy="Ayce-CampaignUrls-CRM"
                                helperText={copiedText === crm && 'Copied to clipboard'}
                            />
                        )}
                    </Grid>
                </Grid>
            )}
            {!isVisible && (
                <Typography data-cy="Ayce-CampaignUrls-Hidden">
                    URL(s) will become available when landing page is set up and this campaign is published
                </Typography>
            )}
        </Box>
    );
}
